import React, { useEffect, useState } from 'react';
import { Minus, Plus, ShoppingBag } from 'lucide-react';
import { MenuItem, MenuItemOption, CartItemOption } from '../../lib/types';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { formatCurrency, cn } from '../../lib/utils';
import { useMenuItemOptions } from '../../lib/hooks';
interface ProductDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem | null;
  onAdd: (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => void;
  primaryColor?: string;
}
export function ProductDetailModal({
  isOpen,
  onClose,
  item,
  onAdd,
  primaryColor
}: ProductDetailModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<Record<string, MenuItemOption[]>>({});
  const {
    options,
    loading
  } = useMenuItemOptions(item?.id);
  // Reset state when modal opens with new item
  useEffect(() => {
    if (isOpen && item) {
      setQuantity(1);
      setNotes('');
      setSelectedOptions({});
    }
  }, [isOpen, item, options]);
  if (!item) return null;
  // Group options by option_group
  const groupedOptions = options.reduce((acc, opt) => {
    if (!acc[opt.option_group]) {
      acc[opt.option_group] = [];
    }
    acc[opt.option_group].push(opt);
    return acc;
  }, {} as Record<string, MenuItemOption[]>);
  // Calculate total price
  const optionsTotal = Object.values(selectedOptions).flat().reduce((sum, opt) => sum + opt.price_delta, 0);
  const totalPrice = (item.price + optionsTotal) * quantity;
  // Validation
  const isValid = Object.entries(groupedOptions).every(([group, groupOptions]) => {
    const isRequired = groupOptions.some(o => o.is_required);
    if (!isRequired) return true;
    const selectedInGroup = selectedOptions[group] || [];
    return selectedInGroup.length > 0;
  });
  const handleOptionToggle = (group: string, option: MenuItemOption, maxSelect: number) => {
    setSelectedOptions(prev => {
      const currentGroupSelections = prev[group] || [];
      const isSelected = currentGroupSelections.some(o => o.id === option.id);
      if (isSelected) {
        return {
          ...prev,
          [group]: currentGroupSelections.filter(o => o.id !== option.id)
        };
      } else {
        if (maxSelect === 1) {
          return {
            ...prev,
            [group]: [option]
          };
        } else if (currentGroupSelections.length < maxSelect) {
          return {
            ...prev,
            [group]: [...currentGroupSelections, option]
          };
        }
        return prev;
      }
    });
  };
  const handleAdd = () => {
    if (!isValid) return;
    const flatOptions: CartItemOption[] = Object.entries(selectedOptions).flatMap(([group, opts]) => opts.map(opt => ({
      option_name: opt.name,
      option_group: group,
      price_delta: opt.price_delta
    })));
    onAdd(item, quantity, flatOptions, notes);
    onClose();
  };
  return <Modal isOpen={isOpen} onClose={onClose} className="sm:max-w-2xl p-0 overflow-hidden bg-[#0B0B0B]" data-id="element-345">
      <div className="flex flex-col h-full max-h-[90vh] text-[#F8F5F0]" data-id="element-346">
        {/* Header Image */}
        {item.image_url && <div className="relative h-72 w-full bg-[#1a1a1a] shrink-0" data-id="element-347">
            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" data-id="element-348" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/80 to-transparent" data-id="element-349" />
          </div>}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-350">
          <div className="mb-6" data-id="element-351">
            <h2 className="text-2xl font-bold text-[#F8F5F0] mb-2" data-id="element-352">
              {item.name}
            </h2>
            <p className="text-[#F8F5F0]/60 leading-relaxed" data-id="element-353">
              {item.description}
            </p>
            <div className="mt-4 font-bold text-xl text-[#F4D35E]" data-id="element-354">
              {formatCurrency(item.price)}
            </div>
          </div>

          {/* Options */}
          {item.is_customizable && !loading && Object.entries(groupedOptions).map(([group, groupOptions]) => {
          const isRequired = groupOptions.some(o => o.is_required);
          const maxSelect = groupOptions[0]?.max_select || 1;
          const selectedCount = (selectedOptions[group] || []).length;
          return <div key={group} className="mb-8 border-t border-white/5 pt-6" data-id="element-355">
                  <div className="flex justify-between items-baseline mb-4" data-id="element-356">
                    <h3 className="font-bold text-lg text-[#F8F5F0]" data-id="element-357">
                      {group}
                    </h3>
                    <div className="flex gap-2" data-id="element-358">
                      {isRequired && selectedCount === 0 && <span className="text-xs font-semibold text-[#E63946] bg-[#E63946]/10 px-2 py-1 rounded-md" data-id="element-359">
                          Requerido
                        </span>}
                      <span className="text-xs font-medium text-[#F8F5F0]/50 bg-[#141414] px-2 py-1 rounded-md" data-id="element-360">
                        {maxSelect === 1 ? 'Elige 1' : `Elige hasta ${maxSelect}`}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-361">
                    {groupOptions.map(option => {
                const isSelected = (selectedOptions[group] || []).some(o => o.id === option.id);
                const isDisabled = !isSelected && selectedCount >= maxSelect;
                return <label key={option.id} className={cn('flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer', isSelected ? 'border-[#F4D35E] bg-[#F4D35E]/10' : 'bg-[#141414] border-white/10 hover:border-white/20', isDisabled && 'opacity-40 cursor-not-allowed')} onClick={() => {
                  if (!isDisabled) handleOptionToggle(group, option, maxSelect);
                }} data-id="element-362">
                          <div className="flex items-center gap-3" data-id="element-363">
                            <div className={cn('flex items-center justify-center border transition-colors', maxSelect === 1 ? 'w-5 h-5 rounded-full' : 'w-5 h-5 rounded', isSelected ? 'border-[#F4D35E] bg-[#F4D35E] text-[#0B0B0B]' : 'border-white/20 bg-transparent')} data-id="element-364">
                              {isSelected && maxSelect > 1 && <div className="w-2.5 h-2.5 bg-[#0B0B0B] rounded-sm" data-id="element-365" />}
                              {isSelected && maxSelect === 1 && <div className="w-2 h-2 bg-[#0B0B0B] rounded-full" data-id="element-366" />}
                            </div>
                            <span className={cn('font-medium', isSelected ? 'text-[#F8F5F0]' : 'text-[#F8F5F0]')} data-id="element-367">
                              {option.name}
                            </span>
                          </div>

                          {option.price_delta > 0 && <span className="text-sm font-medium text-[#F8F5F0]/50" data-id="element-368">
                              +{formatCurrency(option.price_delta)}
                            </span>}
                        </label>;
              })}
                  </div>
                </div>;
        })}

          {/* Notes */}
          <div className="border-t border-white/5 pt-6 mb-6" data-id="element-369">
            <div className="space-y-1.5" data-id="element-370">
              <label className="block text-sm font-medium text-[#F8F5F0]" data-id="element-371">
                Instrucciones especiales
              </label>
              <textarea placeholder="Ej. Sin cebolla, aderezo aparte..." value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="w-full px-3 py-2 rounded-xl border border-white/10 bg-[#141414] text-[#F8F5F0] placeholder-[#F8F5F0]/30 focus:outline-none focus:ring-2 focus:ring-[#F4D35E] resize-none" data-id="element-372" />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 sm:p-6 bg-[#0B0B0B] border-t border-white/10 shrink-0" data-id="element-373">
          <div className="flex items-center gap-4" data-id="element-374">
            {/* Quantity Controls */}
            <div className="flex items-center bg-[#141414] rounded-xl p-1 shrink-0" data-id="element-375">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-[#1a1a1a] transition-all text-[#F8F5F0] disabled:opacity-50" disabled={quantity <= 1} data-id="element-376">
                <Minus className="w-4 h-4" data-id="element-377" />
              </button>
              <span className="w-10 text-center font-semibold text-[#F8F5F0]" data-id="element-378">
                {quantity}
              </span>
              <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-[#1a1a1a] transition-all text-[#F8F5F0]" data-id="element-379">
                <Plus className="w-4 h-4" data-id="element-380" />
              </button>
            </div>

            {/* Add Button */}
            <button className={cn('flex-1 h-12 rounded-xl text-lg font-bold flex items-center justify-center transition-all', isValid ? 'bg-[#E63946] text-white shadow-lg shadow-[#E63946]/30 hover:bg-[#d42f3d]' : 'bg-[#141414] text-[#F8F5F0]/30 cursor-not-allowed')} onClick={handleAdd} disabled={!isValid} data-id="element-381">
              <ShoppingBag className="w-5 h-5 mr-2" data-id="element-382" />
              Agregar • {formatCurrency(totalPrice)}
            </button>
          </div>
        </div>
      </div>
    </Modal>;
}